import React, { useState, useCallback } from "react";
import { useTranslation } from "shared/contexts/I18nContext";
import useStudentEnrollmentData from "shared/hooks/useStudentEnrollmentData";
import useBatchEvaluations from "shared/hooks/useBatchEvaluations";
import useAssignments from "shared/hooks/useAssignments";
import AssessmentsContent from "modules/evaluation/AssignmentsContent";
import { useMasterList } from "shared/hooks/useMasterList";

const CourseHub = ({ navigateTo, selectedCourseTitle, selectedCourseId, useMockData, displayToast }) => {
  const { t } = useTranslation();
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [activeTab, setActiveTab] = useState("students");
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedEvaluation, setSelectedEvaluation] = useState(null);

  const { masterData } = useMasterList({ displayToast });
  const getLabel = useCallback((value, category) => {
    if (!masterData || !masterData[category]) return value;
    const item = masterData[category].find(i => i.value === value);
    return item ? item.label : value;
  }, [masterData]);

  // Fetch batches and students for the course
  const {
    students,
    enrollments,
    batches: fetchedBatches,
    loading: enrollmentLoading,
  } = useStudentEnrollmentData({ courseId: selectedCourseId || null });

  // Filter students based on selected batch
  const batchStudents = enrollments
    ?.filter(e => String(e.batch?.id) === String(selectedBatch?.id))
    ?.map(e => students?.find(s => String(s.id) === String(e.student?.id)))
    ?.filter(Boolean) || [];

  // Fetch evaluations for the selected batch
  const { evaluations, loading: evaluationsLoading } = useBatchEvaluations(selectedBatch?.id); // Do not pass courseId so it filters by batch

  // Fetch assignments to calculate completion metrics
  const { assignments } = useAssignments({ batchId: selectedBatch?.id });
  const completedAssignments = assignments?.filter(a => a.submittedDateTime != null).length || 0;
  const avgCompletion = assignments?.length > 0 ? Math.round((completedAssignments / assignments.length) * 100) : 0;

  return (
    <div className="h-screen bg-green-50 dark:bg-gray-900 flex flex-col overflow-hidden">
      <div className="p-4 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={() => navigateTo("courses")} className="text-gray-500 hover:text-gray-900 dark:hover:text-gray-100">
            ← Back
          </button>
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 truncate max-w-sm">
            {selectedCourseTitle || "Course"} Dashboard
          </h1>
        </div>
        <button
          onClick={() => navigateTo("createBatch", selectedCourseTitle, selectedCourseId)}
          className="px-4 py-2 bg-gray-900 dark:bg-gray-600 text-white rounded-lg text-sm font-semibold hover:bg-gray-800 dark:hover:bg-gray-50 transition-colors"
        >
          + Create Batch
        </button>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Column 1: Master (Batches) */}
        <div className="w-[25%] min-w-[250px] bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 overflow-y-auto scrollbar-hide flex flex-col">
          <div className="h-14 px-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 sticky top-0 z-10 shrink-0 flex items-center">
            <h2 className="text-sm font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wide">Batches</h2>
          </div>
          
          {enrollmentLoading ? (
            <div className="p-6 text-center text-sm text-gray-500">Loading batches...</div>
          ) : fetchedBatches?.length === 0 ? (
            <div className="p-6 text-center text-sm text-gray-500">No batches found.</div>
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-gray-700/50 flex-1">
              {fetchedBatches?.map((batch) => (
                <li 
                  key={batch.id}
                  onClick={() => {
                    setSelectedBatch(batch);
                    setSelectedStudent(null);
                    setSelectedEvaluation(null);
                  }}
                  className={`p-4 cursor-pointer transition-colors ${
                    selectedBatch?.id === batch.id 
                      ? "bg-blue-100 dark:bg-blue-900/40 border-l-4 border-blue-600" 
                      : "hover:bg-gray-50 dark:hover:bg-gray-700/30 border-l-4 border-transparent"
                  }`}
                >
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{batch.batchName}</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
                    {batch.academicClass} {batch.subject}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Column 2: Middle Pane (Tabs: Students | Evaluations) */}
        <div className="w-[35%] min-w-[300px] bg-gray-50 dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 overflow-y-auto scrollbar-hide flex flex-col">
          {!selectedBatch ? (
            <div className="flex-1 flex flex-col items-center justify-center text-gray-500 dark:text-gray-400 p-6 text-center">
              <span className="text-3xl mb-3">👈</span>
              <p className="text-base font-medium">Select a batch to view details</p>
            </div>
          ) : (
            <>
              {/* Tab Header */}
              <div className="h-14 p-0 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 sticky top-0 z-10 shrink-0 flex">
                <button
                  onClick={() => {
                    setActiveTab("students");
                    setSelectedStudent(null);
                    setSelectedEvaluation(null);
                  }}
                  className={`flex-1 h-full text-sm font-bold uppercase tracking-wide text-center border-b-2 transition-colors ${
                    activeTab === "students" 
                      ? "border-blue-600 text-blue-700 dark:text-blue-400" 
                      : "border-transparent text-gray-500 hover:text-gray-900 dark:hover:text-gray-100"
                  }`}
                >
                  Students ({batchStudents.length})
                </button>
                <button
                  onClick={() => {
                    setActiveTab("evaluations");
                    setSelectedStudent(null);
                    setSelectedEvaluation(null);
                  }}
                  className={`flex-1 h-full text-sm font-bold uppercase tracking-wide text-center border-b-2 transition-colors ${
                    activeTab === "evaluations" 
                      ? "border-blue-600 text-blue-700 dark:text-blue-400" 
                      : "border-transparent text-gray-500 hover:text-gray-900 dark:hover:text-gray-100"
                  }`}
                >
                  Evaluations ({evaluations?.length || 0})
                </button>
              </div>

              {/* Tab Content */}
              <div className="flex-1 p-4 overflow-y-auto scrollbar-hide">
                {activeTab === "students" ? (
                  batchStudents.length === 0 ? (
                    <div className="p-6 text-center text-sm text-gray-500">No students enrolled.</div>
                  ) : (
                    <ul className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 divide-y divide-gray-100 dark:divide-gray-700/50">
                      {batchStudents.map(student => (
                        <li key={student.id} 
                            onClick={() => {
                              setSelectedStudent(student);
                              setSelectedEvaluation(null);
                            }}
                            className={`flex items-center gap-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-700/20 cursor-pointer ${
                              selectedStudent?.id === student.id ? "bg-blue-100 dark:bg-blue-900/40" : ""
                            }`}>
                          <img 
                            src={student.profilePictureUrl || `https://placehold.co/32x32/e0f2f7/00796b?text=${student.name?.charAt(0) || '?'}`} 
                            alt={student.name}
                            className="w-10 h-10 rounded-full flex-shrink-0"
                          />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{student.name}</p>
                            <div className="flex flex-col gap-0.5 mt-0.5">
                              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                {getLabel(student.academicClass, 'academicClasses')} • {getLabel(student.curriculum, 'curricula')}
                              </p>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )
                ) : (
                  // Evaluations Tab Content
                  evaluationsLoading ? (
                    <div className="p-6 text-center text-sm text-gray-500">Loading evaluations...</div>
                  ) : !evaluations || evaluations.length === 0 ? (
                    <div className="text-center text-sm text-gray-500 py-10 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl">
                      No evaluations assigned yet.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {evaluations.map(evaluation => (
                        <div key={evaluation.id} 
                             onClick={() => {
                               setSelectedEvaluation(evaluation);
                               setSelectedStudent(null);
                             }}
                             className={`p-4 border rounded-xl transition-colors flex items-center justify-between cursor-pointer ${
                               selectedEvaluation?.id === evaluation.id 
                                 ? "border-blue-600 bg-blue-100 dark:bg-blue-900/40 dark:border-blue-500" 
                                 : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800"
                             }`}>
                          <div>
                            <p className="font-semibold text-gray-900 dark:text-white text-sm">{evaluation.evaluationName || evaluation.name}</p>
                            <p className="text-xs text-gray-500 mt-1">Due: {new Date(evaluation.dueDate || evaluation.expiryDateTime).toLocaleDateString()}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                )}
              </div>
            </>
          )}
        </div>

        {/* Column 3: Detail View */}
        <div className="flex-1 min-w-[350px] bg-white dark:bg-gray-800 overflow-y-auto scrollbar-hide flex flex-col">
          {!selectedBatch ? (
             <div className="flex-1 flex flex-col items-center justify-center text-gray-400 dark:text-gray-600 p-6 text-center">
               <span className="text-4xl mb-3">📊</span>
               <p className="text-sm">Batch details will appear here</p>
             </div>
          ) : (
            <div className="flex-1 flex flex-col overflow-hidden bg-gray-50 dark:bg-gray-900 scrollbar-hide">
                 {selectedStudent ? (
              // Student Detail View
             <div className="flex-1 flex flex-col overflow-hidden">
               <div className="h-14 px-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 sticky top-0 z-10 shrink-0 flex items-center">
                 <h2 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wide">Student Profile</h2>
               </div>
               <div className="flex-1 flex flex-col p-8 overflow-y-auto scrollbar-hide">
                <div className="flex items-center justify-between mb-6 border-b border-gray-200 dark:border-gray-700 pb-6">
                  <div className="flex items-center gap-4">
                    <img 
                      src={selectedStudent.profilePictureUrl || `https://placehold.co/100x100/e0f2f7/00796b?text=${selectedStudent.name?.charAt(0) || '?'}`} 
                      alt={selectedStudent.name}
                      className="w-16 h-16 rounded-full shadow-sm border border-gray-200 dark:border-gray-700"
                    />
                    <div>
                      <h2 className="text-xl font-bold text-gray-900 dark:text-white">{selectedStudent.name}</h2>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                        {getLabel(selectedStudent.academicClass, 'academicClasses')} • {getLabel(selectedStudent.curriculum, 'curricula')}
                      </p>
                    </div>
                  </div>
                  
                  <button 
                    onClick={() => navigateTo("studentHub", selectedStudent.id)}
                    className="px-4 py-2 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 rounded-lg text-xs font-bold shadow-md hover:bg-gray-800 dark:hover:bg-white transition-colors whitespace-nowrap"
                  >
                    View Profile
                  </button>
                </div>

               {/* Student Assignments for this Batch */}
               <div>
                 <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Batch Assignments</h3>
                 <div className="space-y-3">
                   {assignments?.filter(a => String(a.student?.id) === String(selectedStudent.id)).length === 0 ? (
                     <div className="p-4 text-center text-sm text-gray-500 border border-dashed border-gray-300 dark:border-gray-700 rounded-xl">
                       No assignments found for this student in this batch.
                     </div>
                   ) : (
                     assignments?.filter(a => String(a.student?.id) === String(selectedStudent.id))
                       .map(assignment => (
                         <div key={assignment.id} className="p-4 border border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-800/50 flex justify-between items-center">
                           <div>
                             <p className="font-semibold text-gray-900 dark:text-gray-100 text-sm">{assignment.evaluation?.name || assignment.evaluation?.evaluationName || "Assignment"}</p>
                             <p className="text-xs text-gray-500 mt-1">Submitted: {assignment.submittedDateTime ? new Date(assignment.submittedDateTime).toLocaleDateString() : 'Not submitted'}</p>
                           </div>
                           <div className="flex flex-col items-end gap-1">
                             <span className={`inline-block px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider ${
                               assignment.status === "Completed" ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300" :
                               assignment.status === "InReview" ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300" :
                               "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                             }`}>
                               {assignment.status || "Pending"}
                             </span>
                             {assignment.score != null && (
                               <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">Score: {assignment.score}</span>
                             )}
                           </div>
                          </div>
                        ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : selectedEvaluation ? (
             // Embedded Evaluation Detail View (Assignments List)
             <div className="flex-1 overflow-hidden p-0 bg-gray-50 dark:bg-gray-900/50 flex flex-col">
               <div className="h-14 px-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 sticky top-0 z-10 shrink-0 flex items-center">
                 <h2 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wide">Evaluation Details</h2>
               </div>
               <AssessmentsContent 
                 evaluationId={selectedEvaluation.id} 
                 batchId={selectedBatch.id}
                 displayToast={displayToast} 
                 navigateTo={navigateTo} 
                 isEmbedded={true} 
               />
             </div>
           ) : (
             // Default Batch Metrics View
             <div className="flex-1 flex flex-col h-full bg-white dark:bg-gray-800">
               <div className="h-14 px-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 sticky top-0 z-10 shrink-0 flex justify-between items-center">
                 <h2 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wide">Batch Actions & Details</h2>
                 <button 
                   onClick={() => navigateTo("createBatchEvaluation", selectedBatch.id)}
                   className="px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded text-xs font-semibold hover:bg-gray-50 transition-colors"
                 >
                   + Assign Evaluation
                 </button>
               </div>
               <div className="flex-1 p-8 overflow-y-auto scrollbar-hide">
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                   <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col items-center justify-center text-center">
                     <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Total Students</h3>
                     <p className="text-4xl font-black text-gray-900 dark:text-white">{batchStudents.length}</p>
                   </div>
                   <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col items-center justify-center text-center">
                     <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Assigned Evaluations</h3>
                     <p className="text-4xl font-black text-blue-600 dark:text-blue-400">{evaluations?.length || 0}</p>
                   </div>
                   <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col items-center justify-center text-center">
                     <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Avg Completion</h3>
                     <p className="text-4xl font-black text-gray-900 dark:text-white">{avgCompletion}%</p>
                   </div>
                   <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col items-center justify-center text-center">
                     <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Latest Eval</h3>
                     {evaluations?.length > 0 ? (
                       <>
                         <p className="text-sm font-bold text-gray-900 dark:text-white truncate max-w-full mb-1" title={evaluations[0].evaluationName || evaluations[0].name}>
                           {evaluations[0].evaluationName || evaluations[0].name}
                         </p>
                         <p className="text-2xl font-black text-blue-600 dark:text-blue-400">
                           {assignments?.filter(a => a.evaluation?.id === evaluations[0].id && a.submittedDateTime != null).length || 0} / {assignments?.filter(a => a.evaluation?.id === evaluations[0].id).length || 0}
                         </p>
                         <p className="text-xs text-gray-500 mt-1">Completed</p>
                       </>
                     ) : (
                       <p className="text-lg font-medium text-gray-400 mt-2">None</p>
                     )}
                   </div>
                 </div>

                 <div className="mt-12 flex flex-col items-center justify-center text-center">
                   <div className="w-16 h-16 bg-gray-50 dark:bg-gray-900 rounded-full flex items-center justify-center mb-4 border border-gray-200 dark:border-gray-700">
                     <span className="text-2xl">👈</span>
                   </div>
                   <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Select an item</h3>
                   <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm">
                     Click on any student or evaluation from the list on the left to see their complete profile, scores, and analytics.
                   </p>
                 </div>
               </div>
             </div>
          )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CourseHub;
